<template>
  <Teleport to="body">
  <div id="design-request-form-overlay">
    <div class="dr-backdrop" @click="closeForm"></div>

    <div class="dr-dialog" :class="{ 'print-mode': isPrinting }" ref="dialogRef">

      <!-- 헤더 -->
      <div class="dr-header no-print">
        <div class="dr-header-left">
          <i class="ph-bold ph-paper-plane-tilt"></i>
          <h2>설계요청서 작성</h2>
        </div>
        <div class="dr-header-right">
          <button class="dr-btn-icon" @click="printRequest" title="인쇄">
            <i class="ph-bold ph-printer"></i>
          </button>
          <button class="dr-btn-icon" @click="closeForm">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>
      </div>

      <!-- 인쇄용 헤더 -->
      <div class="dr-print-header print-only">
        <div class="dr-print-logo">
          <img src="/imgs/LOGO.png" alt="BODA" style="height:28px;" />
          <span>설계요청서</span>
        </div>
        <div class="dr-print-meta">
          <span>작성자: {{ member_info.name }}</span>
          <span>작성일: {{ todayStr }}</span>
        </div>
      </div>

      <div class="dr-body" ref="printArea">

        <!-- ====== 1. 요청일시 / 발신자 정보 ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-identification-card"></i>
            <h3>발신자 정보</h3>
          </div>
          <div class="dr-info-grid readonly">
            <div class="dr-info-item">
              <label>요청일시</label>
              <span>{{ todayStr }} {{ nowTime }}</span>
            </div>
            <div class="dr-info-item">
              <label>이름</label>
              <span>{{ member_info.name || '-' }}</span>
            </div>
            <div class="dr-info-item">
              <label>ID</label>
              <span>{{ member_info.mb_id || '-' }}</span>
            </div>
            <div class="dr-info-item">
              <label>소속</label>
              <span>{{ senderGroupName || '소속 없음' }}</span>
            </div>
          </div>
        </div>

        <!-- ====== 2. 고객 정보 ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-user"></i>
            <h3>고객 정보</h3>
            <button class="dr-btn-sm no-print" @click="openCustomerPicker" v-if="!isPrinting">
              <i class="ph-bold ph-address-book"></i> 고객 불러오기
            </button>
          </div>
          <div class="dr-form-grid">
            <div class="dr-field full">
              <label>고객명 <em>*</em></label>
              <input type="text" v-model="form.customer_name" placeholder="홍길동" />
            </div>
            <div class="dr-field">
              <label>생년월일</label>
              <input type="text" v-model="form.customer_birth" placeholder="1990-01-01" maxlength="10" @input="formatBirth" />
            </div>
            <div class="dr-field">
              <label>주민등록번호</label>
              <input type="text" v-model="form.customer_rrn" placeholder="000000-0000000" maxlength="14" @input="formatRrn" />
            </div>
            <div class="dr-field">
              <label>직업</label>
              <input type="text" v-model="form.customer_job" placeholder="사무직" />
            </div>
            <div class="dr-field full">
              <label>주소</label>
              <input type="text" v-model="form.customer_address" placeholder="서울시 강남구..." />
            </div>
            <div class="dr-field full">
              <label>고지사항 (병력사항)</label>
              <textarea v-model="form.medical_history" placeholder="병력사항, 입원이력, 수술이력, 복용 약물 등..." rows="3"></textarea>
            </div>
          </div>
        </div>

        <!-- ====== 3. 보험료 범위 ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-currency-krw"></i>
            <h3>보험료 범위</h3>
          </div>
          <div class="dr-price-range">
            <div class="dr-field">
              <label>최소 (만원)</label>
              <div class="dr-input-suffix">
                <input type="number" v-model="form.price_min" placeholder="10" min="0" />
                <span>만원</span>
              </div>
            </div>
            <span class="dr-range-sep">~</span>
            <div class="dr-field">
              <label>최대 (만원)</label>
              <div class="dr-input-suffix">
                <input type="number" v-model="form.price_max" placeholder="15" min="0" />
                <span>만원</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ====== 4. 보험 유형 ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-tag"></i>
            <h3>보험 유형</h3>
          </div>
          <div class="dr-checkbox-group">
            <label v-for="opt in insuranceTypeOptions" :key="opt" class="dr-check-item" :class="{ active: form.insurance_types.includes(opt) }">
              <input type="checkbox" :value="opt" v-model="form.insurance_types" />
              <span>{{ opt }}</span>
            </label>
          </div>
          <div class="dr-field" v-if="form.insurance_types.includes('기타')" style="margin-top:8px;">
            <input type="text" v-model="form.insurance_type_etc" placeholder="기타 유형 입력..." />
          </div>
        </div>

        <!-- ====== 5. 상세설계 요청 (CA 플랜) ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-shield-check"></i>
            <h3>상세설계 요청</h3>
            <button class="dr-btn-sm no-print" @click="openSolutionPicker" v-if="!isPrinting">
              <i class="ph-bold ph-chart-pie-slice"></i> CA에서 불러오기
            </button>
          </div>
          <span class="dr-section-desc" v-if="importedSolution">
            <i class="ph-fill ph-check-circle" style="color:#22C55E;"></i>
            {{ importedSolution.name }} 불러옴
          </span>

          <div v-if="form.plans.length === 0" class="dr-empty">
            <i class="ph ph-shield-slash"></i>
            <p>CA(보장분석)에서 플랜을 불러와 상세설계 정보를 첨부하세요.</p>
          </div>

          <div class="dr-plan-list">
            <div v-for="(plan, pi) in form.plans" :key="pi" class="dr-plan-card">
              <div class="dr-plan-header">
                <div class="dr-plan-company">
                  <img v-if="plan.company_img" :src="plan.company_img" class="dr-company-logo" />
                  <div>
                    <strong>{{ plan.company_name || '보험사 미정' }}</strong>
                    <span>{{ plan.type_label || '' }}</span>
                  </div>
                </div>
                <div class="dr-plan-meta">
                  <span class="dr-plan-price">{{ formatMoney(plan.price) }}원</span>
                  <button class="dr-btn-icon-sm no-print" @click="removePlan(pi)" title="삭제">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
              </div>
              <div class="dr-plan-info-row">
                <span><strong>납기:</strong> {{ plan.pay }}{{ getPayTypeShort(plan.pay_type) }}</span>
                <span><strong>만기:</strong> {{ plan.end }}{{ getEndTypeShort(plan.end_type) }}</span>
                <span v-if="plan.renewable !== undefined"><strong>갱신:</strong> {{ getRenewableLabel(plan.renewable) }}</span>
              </div>
              <div class="dr-coverage-list" v-if="plan.coverages && plan.coverages.length > 0">
                <table class="dr-cov-table">
                  <thead>
                    <tr>
                      <th>보장항목</th>
                      <th>분류</th>
                      <th class="text-right">보장금액(만원)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(cov, ci) in plan.coverages" :key="ci">
                      <td>{{ cov.name }}</td>
                      <td class="dr-cov-chapter">{{ cov.chapter_name || '' }}</td>
                      <td class="text-right">{{ formatMoney(cov.value) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="dr-plan-no-cov">담보 정보 없음</div>
            </div>
          </div>
        </div>

        <!-- ====== 6. 기타 메모 ====== -->
        <div class="dr-section">
          <div class="dr-section-header">
            <i class="ph-fill ph-note-pencil"></i>
            <h3>기타 메모</h3>
          </div>
          <div class="dr-field full">
            <textarea v-model="form.memo" placeholder="추가 요청사항, 참고사항 등..." rows="3"></textarea>
          </div>
        </div>

        <!-- 인쇄용 풋터 -->
        <div class="dr-print-footer print-only">
          <p>본 설계요청서는 참고용이며, 실제 설계와 다를 수 있습니다.</p>
          <p>BODA (보다) · 보험 다이어리</p>
        </div>
      </div>

      <!-- ====== 하단 버튼 ====== -->
      <div class="dr-footer no-print">
        <button class="dr-btn-secondary" @click="closeForm">취소</button>
        <button class="dr-btn-primary" @click="submitRequest" :disabled="submitting">
          <div class="dr-spinner-sm" v-if="submitting"></div>
          <template v-else>
            <i class="ph-bold ph-paper-plane-tilt"></i>
            설계요청서 생성
          </template>
        </button>
      </div>
    </div>

    <!-- ====== 솔루션 선택 다이얼로그 ====== -->
    <Transition name="modal">
      <div v-if="showSolPicker" class="dr-picker-overlay">
        <div class="dr-picker-backdrop" @click="showSolPicker = false"></div>
        <div class="dr-picker-dialog">
          <div class="dr-picker-header">
            <h3>솔루션 선택</h3>
            <button class="dr-btn-icon" @click="showSolPicker = false"><i class="ph-bold ph-x"></i></button>
          </div>
          <div class="dr-picker-search">
            <i class="ph ph-magnifying-glass"></i>
            <input type="text" v-model="solSearchQuery" placeholder="솔루션 이름 또는 고객명 검색..." />
          </div>
          <div class="dr-picker-list">
            <div class="dr-picker-loading" v-if="solLoading">
              <div class="dr-spinner-sm"></div> 불러오는 중...
            </div>
            <div
              v-for="sol in filteredSolutions"
              :key="sol.idx"
              class="dr-picker-item"
              @click="selectSolution(sol)"
            >
              <div class="dr-picker-item-info">
                <span class="dr-sol-name">{{ sol.name }}</span>
                <span class="dr-sol-customer">{{ sol.customer_name || '미지정 고객' }}</span>
              </div>
              <div class="dr-picker-item-meta">
                <span class="dr-sol-type">{{ sol.type === 0 ? '제안' : '분석' }}</span>
                <span class="dr-sol-price">{{ formatMoney(sol.price) }}원</span>
              </div>
            </div>
            <div v-if="!solLoading && filteredSolutions.length === 0" class="dr-picker-empty">
              검색 결과가 없습니다
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ====== 고객 선택 다이얼로그 ====== -->
    <Transition name="modal">
      <div v-if="showCustPicker" class="dr-picker-overlay">
        <div class="dr-picker-backdrop" @click="showCustPicker = false"></div>
        <div class="dr-picker-dialog">
          <div class="dr-picker-header">
            <h3>고객 선택</h3>
            <button class="dr-btn-icon" @click="showCustPicker = false"><i class="ph-bold ph-x"></i></button>
          </div>
          <div class="dr-picker-search">
            <i class="ph ph-magnifying-glass"></i>
            <input type="text" v-model="custSearchQuery" placeholder="이름 또는 연락처로 검색..." />
          </div>
          <div class="dr-picker-list">
            <div class="dr-picker-loading" v-if="custLoading">
              <div class="dr-spinner-sm"></div> 불러오는 중...
            </div>
            <div
              v-for="cust in filteredCustomers"
              :key="cust.idx"
              class="dr-picker-item"
              @click="selectCustomer(cust)"
            >
              <div class="dr-picker-item-info">
                <span class="dr-sol-name">{{ cust.name }}</span>
                <span class="dr-sol-customer">{{ cust.phone || '연락처 없음' }}</span>
              </div>
              <div class="dr-picker-item-meta">
                <span class="dr-sol-type" v-if="cust.birth">{{ cust.birth }}</span>
                <span class="dr-sol-type" v-if="cust.job">{{ cust.job }}</span>
              </div>
            </div>
            <div v-if="!custLoading && filteredCustomers.length === 0" class="dr-picker-empty">
              검색 결과가 없습니다
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ====== 공유 다이얼로그 ====== -->
    <Transition name="modal">
      <div v-if="showShareDialog" class="dr-share-overlay">
        <div class="dr-share-backdrop" @click="showShareDialog = false"></div>
        <div class="dr-share-dialog">
          <div class="dr-share-header">
            <h3>설계요청서 공유</h3>
            <button class="dr-btn-icon" @click="showShareDialog = false"><i class="ph-bold ph-x"></i></button>
          </div>
          <div class="dr-share-body">
            <div class="dr-share-link-box">
              <label>공유 링크</label>
              <div class="dr-share-link-row">
                <input type="text" :value="shareLink" readonly ref="linkInput" />
                <button class="dr-btn-outline" @click="copyLink">
                  <i class="ph-bold ph-copy"></i> 복사
                </button>
              </div>
            </div>

            <div class="dr-share-divider"><span>또는</span></div>

            <div class="dr-share-actions">
              <button class="dr-btn-kakao" @click="shareKakao">
                <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_small.png" alt="카카오" />
                카카오톡으로 공유
              </button>
            </div>

            <div class="dr-share-receiver">
              <label>수신 대상 지정 (선택)</label>
              <div class="dr-form-grid">
                <div class="dr-field">
                  <input type="text" v-model="receiverName" placeholder="수신자 이름" />
                </div>
                <div class="dr-field">
                  <input type="text" v-model="receiverPhone" placeholder="연락처 (010-0000-0000)" />
                </div>
              </div>
              <button class="dr-btn-outline full-w" @click="sendSMS" :disabled="!receiverPhone">
                <i class="ph-bold ph-chat-circle-text"></i> SMS로 링크 전송
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast -->
    <Transition name="toast">
      <div v-if="toast.show" class="dr-toast" :class="toast.type">
        <i :class="toast.type === 'success' ? 'ph-fill ph-check-circle' : 'ph-fill ph-x-circle'"></i>
        <span>{{ toast.message }}</span>
      </div>
    </Transition>
  </div>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, inject, onMounted } from 'vue';

export default {
  name: 'DesignRequestForm',
  props: {
    closeFunction: Function,
    prefillSolution: { type: Object, default: null },
    prefillCustomer: { type: Object, default: null },
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const getInsuCompanyInfo = inject('getInsuCompanyInfo');
    const SMS_send = inject('SMS_send', null);

    const dialogRef = ref(null);
    const printArea = ref(null);
    const linkInput = ref(null);
    const isPrinting = ref(false);
    const submitting = ref(false);

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const nowTime = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

    // Toast
    const toast = reactive({ show: false, message: '', type: 'success' });
    function showToast(msg, type = 'success') {
      toast.message = msg;
      toast.type = type;
      toast.show = true;
      setTimeout(() => toast.show = false, 3000);
    }

    // ─── 발신자 소속 그룹 조회 ───
    const senderGroupName = ref('');
    async function fetchSenderGroup() {
      try {
        const res = await sql_query(`
          SELECT gn.name
          FROM group_member gm
          LEFT JOIN group_node gn ON gm.main_group_idx = gn.idx
          WHERE gm.mb_id = '${member_info.mb_id}' AND gm.status = 'join'
          LIMIT 1
        `);
        if (res?.[0]?.name) senderGroupName.value = res[0].name;
      } catch (e) { /* ignore */ }
    }

    // ─── 보험 유형 옵션 ───
    const insuranceTypeOptions = ['갱신', '비갱신', '간편', '무해지', '운전자', '기타'];

    // Form
    const form = reactive({
      customer_name: '',
      customer_birth: '',
      customer_rrn: '',
      customer_job: '',
      customer_address: '',
      medical_history: '',
      customer_idx: null,
      price_min: null,
      price_max: null,
      insurance_types: [],
      insurance_type_etc: '',
      plans: [],
      memo: '',
      solution_idx: null,
    });

    const importedSolution = ref(null);

    // ─── 고객 Picker ───
    const showCustPicker = ref(false);
    const custSearchQuery = ref('');
    const custLoading = ref(false);
    const customerList = ref([]);

    function openCustomerPicker() {
      showCustPicker.value = true;
      if (customerList.value.length === 0) fetchCustomers();
    }

    async function fetchCustomers() {
      custLoading.value = true;
      try {
        const result = await sql_query(`SELECT * FROM customers WHERE mb_id = '${member_info.mb_id}' ORDER BY created_at DESC`);
        customerList.value = (Array.isArray(result) ? result : []).map(c => ({
          idx: c.idx,
          name: c.name,
          phone: c.phone,
          birth: c.birth,
          rrn: c.rrn,
          job: c.job,
          address: c.address,
          ex_address: c.ex_address,
          med_history: c.med_history || '',
          notes: c.notes || '',
        }));
      } catch (e) {
        console.warn('고객 조회 실패:', e);
      } finally {
        custLoading.value = false;
      }
    }

    const filteredCustomers = computed(() => {
      const q = custSearchQuery.value.toLowerCase().trim();
      if (!q) return customerList.value;
      return customerList.value.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q)
      );
    });

    function selectCustomer(cust) {
      showCustPicker.value = false;
      form.customer_idx = cust.idx;
      form.customer_name = cust.name || '';
      if (cust.birth) form.customer_birth = cust.birth;
      if (cust.rrn) form.customer_rrn = cust.rrn;
      if (cust.job) form.customer_job = cust.job;
      const addr = [cust.address, cust.ex_address].filter(Boolean).join(' ');
      if (addr) form.customer_address = addr;

      // med_history: JSON 배열 → 텍스트
      const parts = [];
      if (cust.med_history) {
        try {
          const arr = typeof cust.med_history === 'string' ? JSON.parse(cust.med_history) : cust.med_history;
          if (Array.isArray(arr)) {
            arr.forEach(m => {
              const line = [m.when, m.why, m.where, m.what].filter(Boolean).join(' / ');
              if (line) parts.push(line);
            });
          }
        } catch (e) { parts.push(String(cust.med_history)); }
      }
      if (cust.notes) parts.push(cust.notes);
      if (parts.length > 0) form.medical_history = parts.join('\n');

      showToast(`"${cust.name}" 고객 정보를 불러왔습니다`);
    }

    // ─── 솔루션 Picker ───
    const showSolPicker = ref(false);
    const solSearchQuery = ref('');
    const solLoading = ref(false);
    const solutionList = ref([]);

    function openSolutionPicker() {
      showSolPicker.value = true;
      if (solutionList.value.length === 0) fetchSolutions();
    }

    async function fetchSolutions() {
      solLoading.value = true;
      try {
        const sql = `SELECT * FROM IS_solution WHERE mb_id = '${member_info.mb_id}' ORDER BY updated_at DESC`;
        const result = await sql_query(sql);
        solutionList.value = (Array.isArray(result) ? result : []).map(row => {
          const client = typeof row.client === 'string' ? JSON.parse(row.client) : row.client;
          return {
            idx: row.idx,
            name: row.name,
            client,
            customer_idx: row.customer_idx,
            customer_name: client?.name || '',
            type: row.type,
            price: row.price,
            updated_at: row.updated_at,
          };
        });
      } catch (e) {
        console.warn('솔루션 조회 실패:', e);
      } finally {
        solLoading.value = false;
      }
    }

    const filteredSolutions = computed(() => {
      const q = solSearchQuery.value.toLowerCase().trim();
      if (!q) return solutionList.value;
      return solutionList.value.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.customer_name || '').toLowerCase().includes(q)
      );
    });

    async function selectSolution(sol) {
      showSolPicker.value = false;
      importedSolution.value = sol;
      form.solution_idx = sol.idx;
      form.customer_idx = sol.customer_idx;

      // 고객 정보 채우기 (solution.client)
      if (sol.client) {
        form.customer_name = sol.client.name || sol.customer_name || '';
        if (sol.client.civ_code) {
          form.customer_rrn = sol.client.civ_code;
        }
      }

      // customer 테이블에서 추가 정보
      if (sol.customer_idx) {
        try {
          const custRows = await sql_query(`SELECT name, nickname, birth, phone, job, rrn, address, ex_address FROM customers WHERE idx = ${sol.customer_idx} LIMIT 1`);
          if (custRows?.[0]) {
            const c = custRows[0];
            if (c.name) form.customer_name = c.name;
            if (c.birth) form.customer_birth = c.birth;
            if (c.rrn) form.customer_rrn = c.rrn;
            if (c.job) form.customer_job = c.job;
            const addr = [c.address, c.ex_address].filter(Boolean).join(' ');
            if (addr) form.customer_address = addr;
          }
        } catch (e) { console.warn('고객 정보 로드 실패:', e); }
      }

      // 플랜+담보 불러오기
      await loadPlansFromSolution(sol.idx);
      showToast(`"${sol.name}" 솔루션을 불러왔습니다`);
    }

    async function loadPlansFromSolution(solutionIdx) {
      try {
        const plans = await sql_query(`SELECT * FROM IS_plan WHERE solution_id = '${solutionIdx}' ORDER BY idx ASC`);
        if (!Array.isArray(plans) || plans.length === 0) return;

        const chapters = await sql_query(`SELECT * FROM IS_chapter WHERE status = 1 ORDER BY no ASC`);
        const chapterMap = {};
        if (Array.isArray(chapters)) {
          chapters.forEach(ch => { chapterMap[ch.idx] = ch.name; });
        }

        const originals = await sql_query(`SELECT idx, name, short_name, chapter_id FROM IS_ex_coverage`);
        const origMap = {};
        if (Array.isArray(originals)) {
          originals.forEach(o => { origMap[o.idx] = o; });
        }

        const newPlans = [];
        for (const p of plans) {
          const companyInfo = getInsuCompanyInfo(p.company);
          const covRows = await sql_query(`SELECT * FROM IS_coverage WHERE plan_id = '${p.idx}' AND visible = 1 ORDER BY idx ASC`);
          const coverages = [];
          if (Array.isArray(covRows)) {
            covRows.forEach(cov => {
              const orig = origMap[cov.original] || {};
              if (cov.is_union || cov.low_show) return;
              coverages.push({
                name: cov.nick || orig.name || '',
                chapter_name: chapterMap[cov.chapter_id || orig.chapter_id] || '',
                value: cov.value,
              });
            });
          }
          newPlans.push({
            company: p.company,
            company_name: companyInfo?.name || p.company_etc || '미정',
            company_img: companyInfo?.short_img || '',
            type_label: getTypeLabel(p.type_id) || p.type || '',
            price: p.price,
            pay: p.pay,
            pay_type: p.pay_type,
            end: p.end,
            end_type: p.end_type,
            renewable: p.renewable,
            coverages,
          });
        }
        form.plans = newPlans;
      } catch (e) {
        console.warn('플랜 불러오기 실패:', e);
        showToast('플랜 불러오기 실패', 'error');
      }
    }

    function removePlan(index) {
      form.plans.splice(index, 1);
    }

    // ─── 유틸 ───
    function formatMoney(val) {
      if (!val && val !== 0) return '0';
      return String(val).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function getPayTypeShort(code) {
      return { '0': '년납', '1': '세납', '2': '일시납' }[String(code)] || '';
    }
    function getEndTypeShort(code) {
      return { '0': '세만기', '1': '년만기', '2': '종신', '3': '' }[String(code)] || '';
    }
    function getRenewableLabel(code) {
      return { '0': '비갱신', '1': '갱신', '2': '일부갱신', '3': '기타' }[String(code)] || '';
    }
    function getTypeLabel(code) {
      const map = { 0:'직접입력', 1:'종합보장', 2:'건강/상해', 3:'단독실손', 4:'종신/정기',
                    5:'연금/저축', 6:'회사/법인', 7:'실버/상속', 8:'자녀/태아', 9:'운전자',
                    10:'치아보험', 11:'골프/레저', 12:'주택/건물' };
      return map[Number(code)] || '';
    }
    function formatBirth() {
      let v = form.customer_birth.replace(/[^0-9]/g, '');
      if (v.length > 4) v = v.slice(0,4) + '-' + v.slice(4);
      if (v.length > 7) v = v.slice(0,7) + '-' + v.slice(7,9);
      form.customer_birth = v;
    }
    function formatRrn() {
      let v = form.customer_rrn.replace(/[^0-9]/g, '');
      if (v.length > 6) v = v.slice(0,6) + '-' + v.slice(6,13);
      form.customer_rrn = v;
    }

    function generateToken() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let token = '';
      for (let i = 0; i < 32; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
      return token;
    }

    // ─── 제출 ───
    const showShareDialog = ref(false);
    const shareLink = ref('');
    const receiverName = ref('');
    const receiverPhone = ref('');
    const createdToken = ref('');

    async function submitRequest() {
      if (!form.customer_name.trim()) {
        showToast('고객명을 입력해주세요', 'error');
        return;
      }
      submitting.value = true;
      try {
        const token = generateToken();
        createdToken.value = token;

        const esc = (s) => (s || '').replace(/'/g, "\\'");
        const planDataJson = JSON.stringify(form.plans).replace(/'/g, "\\'");
        const typesArr = [...form.insurance_types];
        if (typesArr.includes('기타') && form.insurance_type_etc) {
          typesArr.push(form.insurance_type_etc);
        }
        const insuranceTypesJson = JSON.stringify(typesArr).replace(/'/g, "\\'");

        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 30);
        const expiresStr = `${expiresAt.getFullYear()}-${String(expiresAt.getMonth()+1).padStart(2,'0')}-${String(expiresAt.getDate()).padStart(2,'0')} 23:59:59`;

        const sql = `INSERT INTO design_request 
          (token, sender_id, sender_name, sender_group_name,
           customer_name, customer_birth, customer_rrn, customer_job, customer_address, medical_history, customer_idx,
           price_min, price_max, insurance_types,
           plan_data, solution_idx, memo,
           status, created_at, expires_at)
          VALUES (
            '${token}', '${member_info.mb_id}', '${esc(member_info.name)}', '${esc(senderGroupName.value)}',
            '${esc(form.customer_name)}', '${esc(form.customer_birth)}', '${esc(form.customer_rrn)}', '${esc(form.customer_job)}', '${esc(form.customer_address)}', '${esc(form.medical_history)}', ${form.customer_idx || 'NULL'},
            ${form.price_min || 'NULL'}, ${form.price_max || 'NULL'}, '${insuranceTypesJson}',
            '${planDataJson}', ${form.solution_idx || 'NULL'}, '${esc(form.memo)}',
            'pending', NOW(), '${expiresStr}'
          )`;

        await sql_query(sql);

        const baseUrl = window.location.origin;
        shareLink.value = `${baseUrl}/design-request/${token}`;
        showShareDialog.value = true;

        showToast('설계요청서가 생성되었습니다!');
      } catch (e) {
        console.warn('설계요청서 생성 실패:', e);
        showToast('생성 실패: ' + (e.message || ''), 'error');
      } finally {
        submitting.value = false;
      }
    }

    // ─── 공유 ───
    function copyLink() {
      if (linkInput.value) {
        linkInput.value.select();
        document.execCommand('copy');
        showToast('링크가 복사되었습니다');
      }
    }

    function shareKakao() {
      if (!window.Kakao?.isInitialized()) {
        showToast('카카오 SDK 초기화 실패', 'error');
        return;
      }
      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '설계요청서',
          description: `${member_info.name} 님이 ${form.customer_name} 고객의 설계를 요청합니다.`,
          imageUrl: `${window.location.origin}/imgs/LOGO.png`,
          link: {
            mobileWebUrl: shareLink.value,
            webUrl: shareLink.value,
          },
        },
        buttons: [
          {
            title: '설계요청서 확인',
            link: {
              mobileWebUrl: shareLink.value,
              webUrl: shareLink.value,
            },
          },
        ],
      });
    }

    async function sendSMS() {
      if (!receiverPhone.value || !SMS_send) return;
      const cleanPhone = receiverPhone.value.replace(/[^0-9]/g, '');
      if (cleanPhone.length < 10) {
        showToast('올바른 연락처를 입력해주세요', 'error');
        return;
      }
      try {
        if (createdToken.value) {
          const escName = (receiverName.value || '').replace(/'/g, "\\'");
          await sql_query(`UPDATE design_request SET receiver_name='${escName}', receiver_phone='${cleanPhone}' WHERE token='${createdToken.value}'`);
        }
        const text = `[BODA 설계요청서]\n${member_info.name}님이 설계를 요청하였습니다.\n\n고객: ${form.customer_name}\n\n아래 링크를 통해 확인해주세요.\n${shareLink.value}`;
        await SMS_send(cleanPhone, text);
        showToast('SMS가 전송되었습니다');
      } catch (e) {
        showToast('SMS 전송 실패', 'error');
      }
    }

    // ─── 인쇄 ───
    function printRequest() {
      isPrinting.value = true;
      setTimeout(() => {
        window.print();
        setTimeout(() => { isPrinting.value = false; }, 500);
      }, 100);
    }

    // ─── 닫기 ───
    function closeForm() {
      if (props.closeFunction) props.closeFunction();
    }

    // ─── Init ───
    onMounted(() => {
      fetchSenderGroup();
      if (props.prefillSolution) {
        selectSolution(props.prefillSolution);
      }
      if (props.prefillCustomer) {
        const c = props.prefillCustomer;
        form.customer_name = c.name || '';
        form.customer_birth = c.birth || '';
        form.customer_rrn = c.rrn || '';
        form.customer_job = c.job || '';
        form.customer_address = [c.address, c.ex_address].filter(Boolean).join(' ');
        form.customer_idx = c.idx || null;
      }
    });

    return {
      member_info, todayStr, nowTime, senderGroupName,
      form, isPrinting, submitting, insuranceTypeOptions,
      importedSolution,
      showCustPicker, custSearchQuery, custLoading, filteredCustomers,
      openCustomerPicker, selectCustomer,
      showSolPicker, solSearchQuery, solLoading,
      filteredSolutions, openSolutionPicker, selectSolution,
      removePlan,
      formatMoney, getPayTypeShort, getEndTypeShort, getRenewableLabel,
      formatBirth, formatRrn,
      submitRequest, showShareDialog, shareLink, receiverName, receiverPhone,
      copyLink, shareKakao, sendSMS, printRequest, closeForm,
      toast, dialogRef, printArea, linkInput, createdToken,
    };
  }
};
</script>

<style scoped>
/* ====================================
   PRINT
   ==================================== */
@media print {
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  #design-request-form-overlay {
    position: static !important;
    background: none !important;
  }
  .dr-backdrop { display: none !important; }
  .dr-dialog {
    position: static !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    max-height: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    overflow: visible !important;
  }
  .dr-body { overflow: visible !important; max-height: none !important; }
  .dr-plan-card { break-inside: avoid; }
  .dr-section { break-inside: avoid; }
}
@media screen {
  .print-only { display: none !important; }
}

/* ====================================
   OVERLAY
   ==================================== */
#design-request-form-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dr-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
}
.dr-dialog {
  position: relative;
  width: 720px;
  max-width: 95vw;
  max-height: 92vh;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  overflow: hidden;
}

/* ====================================
   HEADER
   ==================================== */
.dr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.dr-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dr-header-left i { font-size: 22px; color: #2D5BFF; }
.dr-header-left h2 { font-size: 18px; font-weight: 800; color: #1a1a2e; margin: 0; }
.dr-header-right { display: flex; gap: 8px; }

.dr-print-header {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #1a1a2e;
}
.dr-print-logo { display: flex; align-items: center; gap: 10px; font-size: 20px; font-weight: 800; color: #1a1a2e; }
.dr-print-meta { display: flex; gap: 16px; font-size: 13px; color: #666; }

/* ====================================
   BODY
   ==================================== */
.dr-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

/* ====================================
   SECTION
   ==================================== */
.dr-section {
  margin-bottom: 28px;
}
.dr-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.dr-section-header i { font-size: 18px; color: #2D5BFF; }
.dr-section-header h3 { font-size: 15px; font-weight: 700; color: #1a1a2e; margin: 0; }
.dr-section-desc { font-size: 12px; color: #999; margin-left: 4px; display: flex; align-items: center; gap: 4px; }

/* ====================================
   INFO GRID (readonly sender info)
   ==================================== */
.dr-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.dr-info-grid.readonly .dr-info-item {
  background: #f8f9fb;
  border-radius: 8px;
  padding: 10px 14px;
}
.dr-info-item label { display: block; font-size: 11px; color: #999; margin-bottom: 2px; }
.dr-info-item span { font-size: 14px; font-weight: 600; color: #333; }

/* ====================================
   PRICE RANGE
   ==================================== */
.dr-price-range {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}
.dr-price-range .dr-field { flex: 1; }
.dr-range-sep {
  font-size: 20px;
  font-weight: 700;
  color: #999;
  padding-bottom: 8px;
}

/* ====================================
   CHECKBOX GROUP (insurance types)
   ==================================== */
.dr-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dr-check-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}
.dr-check-item input[type="checkbox"] { display: none; }
.dr-check-item:hover { border-color: #2D5BFF; color: #2D5BFF; }
.dr-check-item.active {
  background: #EEF2FF;
  border-color: #2D5BFF;
  color: #2D5BFF;
}

/* ====================================
   FORM GRID
   ==================================== */
.dr-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.dr-form-grid.single { grid-template-columns: 1fr; }
.dr-field.full { grid-column: 1 / -1; }
.dr-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
}
.dr-field label em { color: #EF4444; font-style: normal; }
.dr-field input, .dr-field select, .dr-field textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: #fafafa;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.dr-field input:focus, .dr-field select:focus, .dr-field textarea:focus {
  outline: none;
  border-color: #2D5BFF;
  background: #fff;
}
.dr-field textarea { resize: vertical; min-height: 60px; }
.dr-input-suffix {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dr-input-suffix input { flex: 1; }
.dr-input-suffix span { font-size: 13px; color: #666; white-space: nowrap; }

/* ====================================
   IMPORT ROW
   ==================================== */
.dr-import-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dr-import-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #22C55E;
  font-weight: 600;
}
.dr-import-status i { font-size: 16px; }

/* ====================================
   PLAN CARDS
   ==================================== */
.dr-plan-list { display: flex; flex-direction: column; gap: 12px; }
.dr-plan-card {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
  background: #fafbfc;
}
.dr-plan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #eee;
}
.dr-plan-company { display: flex; align-items: center; gap: 10px; }
.dr-company-logo { width: 32px; height: 32px; border-radius: 8px; object-fit: contain; background: #fff; border: 1px solid #eee; }
.dr-plan-company strong { font-size: 14px; color: #1a1a2e; display: block; }
.dr-plan-company span { font-size: 12px; color: #888; }
.dr-plan-meta { display: flex; align-items: center; gap: 8px; }
.dr-plan-price { font-size: 15px; font-weight: 700; color: #2D5BFF; }
.dr-plan-info-row {
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
}
.dr-plan-info-row strong { color: #444; }

/* ====================================
   COVERAGE TABLE
   ==================================== */
.dr-cov-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dr-cov-table th {
  padding: 6px 16px;
  text-align: left;
  font-weight: 600;
  color: #888;
  font-size: 11px;
  text-transform: uppercase;
  border-bottom: 1px solid #eee;
  background: #f8f9fb;
}
.dr-cov-table td {
  padding: 5px 16px;
  border-bottom: 1px solid #f5f5f5;
  color: #444;
}
.dr-cov-table .text-right { text-align: right; }
.dr-cov-chapter { font-size: 11px; color: #aaa; }
.dr-plan-no-cov { padding: 12px 16px; font-size: 13px; color: #bbb; text-align: center; }

/* ====================================
   EMPTY
   ==================================== */
.dr-empty {
  text-align: center;
  padding: 32px 16px;
  color: #bbb;
}
.dr-empty i { font-size: 32px; margin-bottom: 8px; display: block; }
.dr-empty p { margin: 0; font-size: 13px; }

/* ====================================
   FOOTER
   ==================================== */
.dr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.dr-print-footer {
  text-align: center;
  padding: 20px 0;
  margin-top: 24px;
  border-top: 1px solid #ddd;
  font-size: 11px;
  color: #999;
}
.dr-print-footer p { margin: 2px 0; }

/* ====================================
   BUTTONS
   ==================================== */
.dr-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #2D5BFF;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}
.dr-btn-primary:hover { background: #1a4ae0; }
.dr-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.dr-btn-secondary {
  padding: 10px 20px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.dr-btn-secondary:hover { background: #e5e5e5; }

.dr-btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #fff;
  color: #2D5BFF;
  border: 1px solid #2D5BFF;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.dr-btn-outline:hover { background: #2D5BFF; color: #fff; }
.dr-btn-outline.full-w { width: 100%; justify-content: center; margin-top: 8px; }
.dr-btn-outline:disabled { opacity: 0.4; cursor: not-allowed; }

.dr-btn-sm {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #EEF2FF;
  color: #2D5BFF;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
}
.dr-btn-sm:hover { background: #DBEAFE; }

.dr-btn-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  color: #666;
  font-size: 18px;
}
.dr-btn-icon:hover { background: #e5e5e5; }

.dr-btn-icon-sm {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #ccc;
  font-size: 14px;
}
.dr-btn-icon-sm:hover { color: #EF4444; background: #FEE2E2; }

.dr-btn-kakao {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #FEE500;
  color: #3C1E1E;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  justify-content: center;
}
.dr-btn-kakao img { width: 20px; height: 20px; }
.dr-btn-kakao:hover { background: #FDD800; }

/* ====================================
   SHARE DIALOG
   ==================================== */
.dr-share-overlay, .dr-picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 9500;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dr-share-backdrop, .dr-picker-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
}
.dr-share-dialog, .dr-picker-dialog {
  position: relative;
  width: 420px;
  max-width: 90vw;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  overflow: hidden;
}
.dr-share-header, .dr-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.dr-share-header h3, .dr-picker-header h3 { margin: 0; font-size: 16px; font-weight: 700; }
.dr-share-body { padding: 20px; }

.dr-share-link-box { margin-bottom: 16px; }
.dr-share-link-box label { font-size: 12px; font-weight: 600; color: #555; margin-bottom: 6px; display: block; }
.dr-share-link-row { display: flex; gap: 8px; }
.dr-share-link-row input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  background: #f8f8f8;
  color: #333;
}

.dr-share-divider {
  text-align: center;
  margin: 16px 0;
  position: relative;
}
.dr-share-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e0e0e0;
}
.dr-share-divider span {
  position: relative;
  background: #fff;
  padding: 0 12px;
  font-size: 12px;
  color: #999;
}
.dr-share-actions { margin-bottom: 20px; }
.dr-share-receiver { border-top: 1px solid #f0f0f0; padding-top: 16px; }
.dr-share-receiver label { font-size: 12px; font-weight: 600; color: #555; margin-bottom: 8px; display: block; }

/* ====================================
   PICKER DIALOG
   ==================================== */
.dr-picker-dialog { max-height: 70vh; display: flex; flex-direction: column; width: 480px; }
.dr-picker-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.dr-picker-search i { font-size: 18px; color: #999; }
.dr-picker-search input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
}
.dr-picker-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.dr-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}
.dr-picker-item:hover { background: #F5F7FA; }
.dr-picker-item-info { display: flex; flex-direction: column; gap: 2px; }
.dr-sol-name { font-size: 14px; font-weight: 600; color: #1a1a2e; }
.dr-sol-customer { font-size: 12px; color: #888; }
.dr-picker-item-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.dr-sol-type { font-size: 11px; color: #2D5BFF; background: #EEF2FF; padding: 2px 8px; border-radius: 4px; font-weight: 600; }
.dr-sol-price { font-size: 12px; color: #666; }
.dr-picker-empty, .dr-picker-loading {
  text-align: center;
  padding: 24px;
  font-size: 13px;
  color: #999;
}
.dr-picker-loading { display: flex; align-items: center; justify-content: center; gap: 8px; }

/* ====================================
   TOAST
   ==================================== */
.dr-toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}
.dr-toast.success { background: #DCFCE7; color: #166534; }
.dr-toast.error { background: #FEE2E2; color: #991B1B; }

/* ====================================
   SPINNER
   ==================================== */
.dr-spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: dr-spin 0.7s linear infinite;
}
@keyframes dr-spin { to { transform: rotate(360deg); } }

/* ====================================
   TRANSITIONS
   ==================================== */
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s, transform 0.2s; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.95); }
.toast-enter-active { transition: opacity 0.3s, transform 0.3s; }
.toast-leave-active { transition: opacity 0.2s, transform 0.2s; }
.toast-enter-from { opacity: 0; transform: translateX(-50%) translateY(20px); }
.toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

/* ====================================
   MOBILE
   ==================================== */
@media (max-width: 600px) {
  .dr-dialog { max-width: 100vw; max-height: 100vh; border-radius: 0; }
  .dr-form-grid { grid-template-columns: 1fr; }
  .dr-plan-info-row { flex-wrap: wrap; gap: 8px; }
}
</style>
